<template>
  <div
    class="modal fade"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    id="modulePropBoardModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Create a new {{ moduleStore.selectedModuleCreator?.moduleName }}
          </h5>

          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body component-property-body">
          <!-- Properties List -->
          <div class="property-table-wrapper">
            <table class="table table-borderless mb-0 w-auto m-auto align-middle">
            <tbody class="text-end">
              <tr
                v-for="(property, key) in moduleStore.selectedModuleCreator?.pendingModule?.properties"
                :key="key"
              >
                <td class="property-label">{{ property.name || key }}:</td>
                <td>
                  <div class="input-group flex-nowrap property-input-group">
                    <input
                      class="form-control"
                      type="number"
                      :min="property.min ?? 0"
                      :max="property.max ?? undefined"
                      :step="property.step ?? 'any'"
                      :value="property.value"
                      @input="onValueChange(property, $event.target.value)"
                      @wheel.prevent.stop="onWheel(property, $event)"
                    />
                    <span
                      v-if="typeof property.unit === 'string' && property.unit.length > 0"
                      class="input-group-text"
                    >
                      {{ property.unit }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <!-- Preview Area -->
          <div id="module-preview" class="d-flex align-items-center justify-content-center">
            <svg
              width="100%"
              height="100%"
              :viewBox="`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`"
              xmlns="http://www.w3.org/2000/svg"
              fill="dodgerblue"
              stroke="dodgerblue"
            >
              
              <SvgModuleVue
                v-if="hasPendingModule"
                :id="previewSvgId"
                :topLeftCorner="moduleStore.selectedModuleCreator.pendingModule.elementsToRender.topLeftCorner"
                :borderShapes="moduleStore.selectedModuleCreator.pendingModule.elementsToRender.borderShapes"
                :maskShapes="moduleStore.selectedModuleCreator.pendingModule.elementsToRender.maskShapes"
                :centerPoint="moduleStore.selectedModuleCreator.pendingModule.elementsToRender.centerPoint"
                :hightlightOffset="0"
              />
            </svg>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn btn-secondary"
            data-bs-target="#moduleBoardModal"
            data-bs-toggle="modal"
          >
            Back
          </button>

          <button
            class="btn btn-outline-primary"
            type="button"
            @click="saveAsTemplate"
          >
            Save as template
          </button>

          <button
            class="btn btn-success"
            data-bs-dismiss="modal"
            @click="controlStore.startModulePlacement"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from "vue";
import { useControlStore } from "@/stores/control";
import { useMoudleStore } from "@/stores/module";
import SvgModuleVue from "@/components/SvgModule.vue";
import { parseSVG, makeAbsolute } from "svg-path-parser";

export default defineComponent({
  components: { SvgModuleVue },
  setup() {
    const moduleStore = useMoudleStore();
    const controlStore = useControlStore();

    
    const previewSvgId = ref(`module-preview-${Math.random().toString(36).slice(2)}`);

    
    watch(
      () => moduleStore.selectedModuleCreator,
      () => {
        previewSvgId.value =`modal-preview-${Math.random().toString(36).slice(2)}`;
      },
      { immediate: true }
    );

    const hasPendingModule = computed(() => {
      const m = moduleStore.selectedModuleCreator?.pendingModule;
      const e = m?.elementsToRender;
      return (
        !!m &&
        !!e &&
        Array.isArray(e.topLeftCorner) &&
        Array.isArray(e.centerPoint) &&
        Array.isArray(e.borderShapes) &&
        Array.isArray(e.maskShapes)
      );
    });

    const decimalPlaces = (value) => {
      const text = String(value);
      if (text.includes("e-")) return Number(text.split("e-")[1]) || 0;
      const dot = text.indexOf(".");
      return dot >= 0 ? text.length - dot - 1 : 0;
    };

    const clampValue = (prop, value) => {
      let next = Number(value);
      if (!Number.isFinite(next)) next = 0;
      next = Math.max(typeof prop.min === "number" ? prop.min : 0, next);
      if (typeof prop.max === "number") next = Math.min(prop.max, next);
      return next;
    };

    const onValueChange = (prop, newValue) => {
      prop.value = clampValue(prop, newValue);
      moduleStore.selectedModuleCreator?.updatePendingModule?.();
    };

    const onWheel = (prop, e) => {
      const step = Number(prop.step) || 1;
      const direction = e.deltaY < 0 ? 1 : -1;
      const precision = Math.max(decimalPlaces(step), decimalPlaces(prop.value));
      const factor = 10 ** Math.min(precision, 12);
      const current = Math.round(Number(prop.value || 0) * factor);
      const delta = Math.round(step * factor) * direction;
      prop.value = clampValue(prop, (current + delta) / factor);
      moduleStore.selectedModuleCreator?.updatePendingModule?.();
    };

    
    const viewBox = computed(() => {
  const m = moduleStore.selectedModuleCreator?.pendingModule;
  if (!m) return { x: 0, y: 0, w: 150, h: 100 };

  const e = m.elementsToRender;
  const shapes = [
    ...(e?.borderShapes || []),
    ...(e?.maskShapes || [])
  ];

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const s of shapes) {
    // Rectangle
    if (typeof s.x === "number" && typeof s.y === "number") {
      const x0 = s.x;
      const y0 = s.y;
      const x1 = s.x + (s.width ?? 0);
      const y1 = s.y + (s.height ?? 0);
      minX = Math.min(minX, x0, x1);
      minY = Math.min(minY, y0, y1);
      maxX = Math.max(maxX, x0, x1);
      maxY = Math.max(maxY, y0, y1);
      continue;
    }

    // Circle
    if (typeof s.cx === "number" && typeof s.cy === "number") {
      const r = s.r ?? 0;
      minX = Math.min(minX, s.cx - r);
      maxX = Math.max(maxX, s.cx + r);
      minY = Math.min(minY, s.cy - r);
      maxY = Math.max(maxY, s.cy + r);
      continue;
    }

    // Polygon
    if (Array.isArray(s.points)) {
      for (const p of s.points) {
        const px = p[0], py = p[1];
        minX = Math.min(minX, px);
        maxX = Math.max(maxX, px);
        minY = Math.min(minY, py);
        maxY = Math.max(maxY, py);
      }
      continue;
    }

    // Path
    if (typeof s.d === "string") {
      try {
        const pathSegments = makeAbsolute(parseSVG(s.d));
        for (const seg of pathSegments) {
          if (typeof seg.x === "number" && typeof seg.y === "number") {
            minX = Math.min(minX, seg.x);
            maxX = Math.max(maxX, seg.x);
            minY = Math.min(minY, seg.y);
            maxY = Math.max(maxY, seg.y);
          }
        }
      } catch (e) {
        console.warn("Failed to parse path for bbox", e);
      }
      continue;
    }


    
  }

  
  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    const tl = e?.topLeftCorner;
    const cp = e?.centerPoint;

    if (Array.isArray(tl) && Array.isArray(cp)) {
      
      const halfW = Math.abs(cp[0] - tl[0]);
      const halfH = Math.abs(cp[1] - tl[1]);
      const pad = Math.max(300, Math.max(halfW, halfH) * 0.2);

      const x = cp[0] - halfW - pad;
      const y = cp[1] - halfH - pad;
      const w = (halfW + pad) * 2;
      const h = (halfH + pad) * 2;
      return { x, y, w, h };
    }

    
    return { x: 0, y: 0, w: 150, h: 100 };
  }

  // padding
  const pad = 300;
  minX -= pad; minY -= pad; maxX += pad; maxY += pad;

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
});

    const saveAsTemplate = () => {
      console.log("[ComponentPropertyBoard] saveAsTemplate clicked");
      const creator = moduleStore.selectedModuleCreator;
      if (!creator) return;

      const defaultName =
        creator.displayName || creator.moduleName || "My Custom Component";

      const name = window.prompt("Template name?", defaultName);
      if (!name) return;

      moduleStore.createUserTemplateFromPending(name);
    };

    return {
      moduleStore,
      controlStore,
      onValueChange,
      onWheel,
      viewBox,
      saveAsTemplate,
      previewSvgId,
      hasPendingModule
    };
  }
});
</script>

<style scoped>
#module-preview {
  width: 100%;
  height: 200px;
  max-width: 400px;
  max-height: 400px;
  overflow: auto;
}

.component-property-body {
  display: grid;
  grid-template-columns: max-content minmax(220px, 400px);
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
}

.property-table-wrapper {
  flex: 0 0 auto;
  min-width: 0;
}

.property-label {
  white-space: nowrap;
}

.property-input-group {
  width: clamp(180px, 28vw, 260px);
}

@media (max-width: 900px) {
  .modal-lg {
    max-width: calc(100vw - 1.5rem);
  }

  .component-property-body {
    grid-template-columns: max-content minmax(180px, 32vw);
    gap: 1rem;
  }

  .property-input-group {
    width: clamp(160px, 30vw, 220px);
  }
}
</style>
